'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, GFPGANRestoreResponse } from '@/lib/ai-models-api';

export default function GFPGANPage() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [upscale, setUpscale] = useState(2);
  const [onlyCenterFace, setOnlyCenterFace] = useState(false);
  const [model, setModel] = useState('GFPGANv1.4');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GFPGANRestoreResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      const response = await aiModelsApi.gfpganRestore(image, {
        upscale,
        only_center_face: onlyCenterFace,
        model,
      });

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">GFPGAN - Face Restoration</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Model Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">Model</label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="GFPGANv1.4">V1.4 - More details, better identity</option>
                    <option value="GFPGANv1.3">V1.3 - More natural restoration</option>
                  </select>
                </div>

                {/* Upscale Factor */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Upscale Factor: {upscale}x
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="4"
                    value={upscale}
                    onChange={(e) => setUpscale(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
                    <span>1x</span>
                    <span>2x</span>
                    <span>3x</span>
                    <span>4x</span>
                  </div>
                </div>

                {/* Only Center Face */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="onlyCenterFace"
                    checked={onlyCenterFace}
                    onChange={(e) => setOnlyCenterFace(e.target.checked)}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <label htmlFor="onlyCenterFace" className="text-sm">
                    Only restore center face
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Restore Faces'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Results</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image and click &quot;Restore Faces&quot;</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Restoring faces...</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Restored Image */}
                    <div>
                      <p className="text-sm text-[var(--text-muted)] mb-2">Restored Image</p>
                      <img
                        src={`data:image/png;base64,${result.image}`}
                        alt="Restored"
                        className="w-full rounded-lg"
                      />
                    </div>

                    {/* Download Button */}
                    <a
                      href={`data:image/png;base64,${result.image}`}
                      download="gfpgan_restored.png"
                      className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-center"
                    >
                      Download Restored Image
                    </a>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Faces Found</p>
                        <p className="text-lg font-semibold">{result.faces_detected}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Restored</p>
                        <p className="text-lg font-semibold">{result.faces_restored}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Output Size</p>
                        <p className="text-lg font-semibold">{result.restored_size[0]}x{result.restored_size[1]}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Inference</p>
                        <p className="text-lg font-semibold">{result.inference_time_ms.toFixed(0)}ms</p>
                      </div>
                    </div>

                    {/* Additional Stats */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Original Size</p>
                        <p className="text-sm font-medium">{result.original_size[0]} x {result.original_size[1]}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Upscale</p>
                        <p className="text-sm font-medium">{result.upscale}x</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Model</p>
                        <p className="text-sm font-medium">{result.model}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
